import { Button, Stack, TextField, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { createUnauthenticatedPostgrest } from "../../utils/unauthenticatedApi";
import { AIRPLX_TOKEN } from "../../containers/IdentityContainer";
import { Loading } from "../../widgets/Loading";
const logo = "/airplx-logo-transparent-square-no-text.png";

export const ResetPassword: React.FC<{}> = () => {
  const { t } = useTranslation();
  const { code } = useParams();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = React.useState<string>("");
  const [confirmPassword, setConfirmPassword] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");

  // Create unauthenticated postgrest client for public endpoint
  const postgrest = React.useMemo(() => {
    return createUnauthenticatedPostgrest();
  }, []);

  const validatePassword = (): boolean => {
    if (newPassword.length < 8) {
      setError(t("screens.resetPassword.errors.tooShort"));
      return false;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setError(t("screens.resetPassword.errors.needsUppercase"));
      return false;
    }
    if (!/[a-z]/.test(newPassword)) {
      setError(t("screens.resetPassword.errors.needsLowercase"));
      return false;
    }
    if (!/[0-9]/.test(newPassword)) {
      setError(t("screens.resetPassword.errors.needsNumber"));
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError(t("screens.resetPassword.errors.noMatch"));
      return false;
    }
    return true;
  };

  const handleResetPassword = async () => {
    setError("");

    if (!validatePassword()) {
      return;
    }

    setLoading(true);

    try {
      const { data: token } = await postgrest.rpc("reset_password_with_code", {
        p_code: code,
        p_new_password: newPassword,
      });

      if (!token || token.length === 0) {
        setError(t("screens.resetPassword.errors.invalidOrExpired"));
        setLoading(false);
        return;
      }

      // Store token and reload (auto-login)
      // Use reload instead of navigate to ensure React state reinitializes
      localStorage.setItem(AIRPLX_TOKEN, token[0]);
      window.location.reload();
    } catch (err: any) {
      setError(err.message || t("screens.resetPassword.errors.genericError"));
      console.error("Error resetting password:", err);
      setLoading(false);
    }
  };

  const getPasswordStrength = (): string => {
    if (!newPassword) return "";
    if (newPassword.length < 8) return t("screens.resetPassword.passwordStrength.tooShort");

    let strength = 0;
    if (/[A-Z]/.test(newPassword)) strength++;
    if (/[a-z]/.test(newPassword)) strength++;
    if (/[0-9]/.test(newPassword)) strength++;
    if (/[^A-Za-z0-9]/.test(newPassword)) strength++;

    if (strength === 4 && newPassword.length >= 12) return t("screens.resetPassword.passwordStrength.strong");
    if (strength >= 3) return t("screens.resetPassword.passwordStrength.good");
    if (strength >= 2) return t("screens.resetPassword.passwordStrength.fair");
    return t("screens.resetPassword.passwordStrength.weak");
  };

  const passwordStrength = getPasswordStrength();

  if (!code) {
    return (
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        height="80vh"
        width="100%"
      >
        <Typography variant="h5">{t("screens.resetPassword.invalidLink.title")}</Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/login")}
          sx={{ mt: 2 }}
        >
          {t("screens.resetPassword.invalidLink.backButton")}
        </Button>
      </Stack>
    );
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        height="60vh"
        width="100vw"
      >
        <Stack direction="column" width={450} spacing={3}>
          <Stack spacing={1} alignItems="center">
            <Typography variant="h5">{t("screens.resetPassword.title")}</Typography>
            <Typography variant="body1" color="text.secondary" align="center">
              {t("screens.resetPassword.subtitle")}
            </Typography>
          </Stack>

          <TextField
            fullWidth
            label={t("screens.resetPassword.newPasswordLabel")}
            variant="outlined"
            type="password"
            onChange={(e) => setNewPassword(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleResetPassword()}
            value={newPassword}
            autoFocus
          />

          {newPassword && (
            <Typography
              variant="body2"
              sx={{ mt: -1 }}
              color={
                passwordStrength === t("screens.resetPassword.passwordStrength.strong") ? "success.main" :
                passwordStrength === t("screens.resetPassword.passwordStrength.good") ? "primary.main" :
                passwordStrength === t("screens.resetPassword.passwordStrength.fair") ? "warning.main" :
                "error.main"
              }
            >
              {t("screens.resetPassword.strengthLabel", { strength: passwordStrength })}
            </Typography>
          )}

          <TextField
            fullWidth
            label={t("screens.resetPassword.confirmPasswordLabel")}
            variant="outlined"
            type="password"
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleResetPassword()}
            value={confirmPassword}
            error={Boolean(error)}
            helperText={error}
            sx={{ mt: newPassword ? 0 : 2 }}
          />

          <Typography variant="caption" color="text.secondary" align="center">
            {t("screens.resetPassword.requirementsText")}
          </Typography>

          <Button
            disabled={!newPassword || !confirmPassword || loading}
            variant="contained"
            color="primary"
            onClick={handleResetPassword}
            fullWidth
            sx={{ borderRadius: 1 }}
          >
            {loading ? t("screens.resetPassword.resettingButton") : t("screens.resetPassword.resetButton")}
          </Button>

          <Button
            size="small"
            onClick={() => navigate("/login")}
          >
            {t("screens.resetPassword.backToLogin")}
          </Button>
        </Stack>
      </Stack>
      <img
        src={logo}
        style={{ position: "fixed", bottom: 15, right: 15, width: 50 }}
      />
    </>
  );
};
